import { useGetIdentity, useLogout } from '@refinedev/core';
import { BarChart3, CreditCard, KeyRound, LogOut, ScrollText, ShoppingCart } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface Identity {
  id: string;
  email: string;
  name: string;
  roles: string[];
  permissions: string[];
}

// Q-246: Fila de Compras é a home (Nielsen #6: a tarefa do dia é a landing);
// Dashboard virou "Visão geral" em /visao-geral. Auditoria só pra admin.
const NAV = [
  { to: '/', label: 'Fila de Compras', icon: ShoppingCart, adminOnly: false },
  { to: '/contas', label: 'Contas PSN', icon: KeyRound, adminOnly: false },
  { to: '/topup', label: 'Gift cards', icon: CreditCard, adminOnly: false },
  { to: '/visao-geral', label: 'Visão geral', icon: BarChart3, adminOnly: false },
  { to: '/audit', label: 'Auditoria', icon: ScrollText, adminOnly: true },
] as const;

function isAdmin(identity: Identity | undefined): boolean {
  if (!identity) return false;
  if (identity.permissions?.includes('*')) return true;
  return (identity.roles ?? []).some((r) => r === 'admin' || r === 'owner');
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const { data: identity } = useGetIdentity<Identity>();
  const { mutate: logout } = useLogout();

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="flex w-64 flex-col border-r border-border bg-card">
        <div className="flex h-16 items-center border-b border-border px-6">
          <span className="text-lg font-semibold tracking-tight">
            <span className="text-primary">●</span> Automation
          </span>
        </div>

        <nav className="flex-1 space-y-1 p-3">
          {NAV.filter((item) => !item.adminOnly || isAdmin(identity)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-secondary text-secondary-foreground'
                    : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground',
                )
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-3">
          <div className="mb-2 px-3 py-2 text-xs">
            <div className="font-medium text-foreground truncate">
              {identity?.name ?? identity?.email ?? '...'}
            </div>
            <div className="text-muted-foreground truncate">{identity?.email}</div>
            {identity?.roles && identity.roles.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {identity.roles.map((r) => (
                  <span
                    key={r}
                    className="rounded bg-secondary px-1.5 py-0.5 text-[10px] text-secondary-foreground"
                  >
                    {r}
                  </span>
                ))}
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-muted-foreground"
            onClick={() => logout()}
          >
            <LogOut className="h-4 w-4" />
            Sair
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="container mx-auto py-8">{children}</div>
      </main>
    </div>
  );
}
